
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Shield, 
  Scale, 
  AlertTriangle, 
  CheckCircle, 
  FileText, 
  Phone, 
  Clock,
  Users,
  Gavel,
  Home
} from 'lucide-react';

const RoofLifeExpectancyReport: React.FC = () => {
  const handleContactUs = () => {
    window.open('https://carolinatrophyroofs.sky-quote.com/schedule#step1', '_blank');
  };

  const handleCallNow = () => {
    window.location.href = 'tel:252-371-2266';
  };

  const keyFeatures = [
    {
      icon: FileText,
      title: 'Thorough Inspections',
      description: 'Our certified professionals examine every aspect of your roof\'s condition.'
    },
    {
      icon: CheckCircle,
      title: 'Detailed Reports',
      description: 'We provide comprehensive documentation with photographic evidence.'
    },
    {
      icon: Clock,
      title: 'Expert Opinions',
      description: 'Professional estimates of your roof\'s remaining lifespan under normal conditions.'
    },
    {
      icon: Users,
      title: 'Maintenance Recommendations',
      description: 'We offer advice on how to extend your roof\'s life through proper care.'
    }
  ];

  const referenceUrls = [
    { name: 'NC Department of Insurance - Consumer Services', url: 'https://www.ncdoi.gov/consumers' },
    { name: 'NC General Assembly - Statutes', url: 'https://www.ncleg.gov/Laws/GeneralStatutes' },
    { name: 'NC State Bar - Consumer Information', url: 'https://www.ncbar.gov/for-the-public/' },
    { name: 'National Association of Insurance Commissioners', url: 'https://content.naic.org/consumer.htm' },
    { name: 'Federal Trade Commission - Homeowners Insurance', url: 'https://www.consumer.ftc.gov/articles/understanding-homeowners-insurance' },
    { name: 'NC Attorney General - Consumer Protection', url: 'https://ncdoj.gov/protecting-consumers/' },
    { name: 'National Roofing Contractors Association', url: 'https://www.nrca.net/homeowner-resources' },
    { name: 'International Association of Certified Home Inspectors', url: 'https://www.nachi.org/roof-inspection.htm' }
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative py-16 px-4 sm:px-6 lg:px-8 bg-brand-charcoal-gray text-white">
          <div className="max-w-6xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 px-4 py-2 text-sm font-medium border-brand-trophy-gold text-brand-trophy-gold">
              <Shield className="w-4 h-4 mr-2" />
              Insurance Protection Service
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Roof Life Expectancy Report
              <span className="text-brand-trophy-gold block mt-2">Fight Unfair Insurance Cancellations</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mb-8 mx-auto">
              Are you a North Carolina homeowner facing the threat of losing your insurance coverage due to an aging roof? 
              Don't let insurance companies bully you into unnecessary and expensive roof replacements.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleCallNow}
                size="lg"
                className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-3"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call (252) 371-2266
              </Button>
              <Button 
                onClick={handleContactUs}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-3"
              >
                Get Your Report
              </Button>
            </div>
          </div>
        </section>

        {/* Alert Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Alert className="border-orange-200 bg-orange-50">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <AlertDescription className="text-orange-800 text-lg">
                <strong>Serving homeowners in:</strong> Rocky Mount, Greenville, Wilson, Goldsboro, Raleigh, 
                Jacksonville, and surrounding Eastern NC areas who are receiving alarming letters from 
                insurance carriers demanding immediate roof replacements.
              </AlertDescription>
            </Alert>
          </div>
        </section>

        {/* Understanding NC Laws */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Scale className="w-12 h-12 text-brand-trophy-gold mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Understanding North Carolina Insurance Laws
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Know your rights under North Carolina law before making costly decisions.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-blue-200 bg-blue-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center text-blue-900">
                    <Gavel className="w-6 h-6 mr-3" />
                    NC General Statute 58-3-33
                  </CardTitle>
                  <CardDescription className="text-blue-700">
                    Cancellation Restrictions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-800">
                    North Carolina law provides significant protections for homeowners against arbitrary 
                    insurance cancellations. Insurance companies are required to provide specific reasons 
                    for cancellation or nonrenewal of policies.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 bg-green-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center text-green-900">
                    <CheckCircle className="w-6 h-6 mr-3" />
                    Fair Assessment Requirements
                  </CardTitle>
                  <CardDescription className="text-green-700">
                    Legal Precedent Protection
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-green-800">
                    In <em>Harleysville Worcester Ins. Co. v. Pinnacle Group, Inc.</em>, the court emphasized 
                    the importance of fair and reasonable assessments based on actual inspections rather 
                    than arbitrary age limits.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* The 20-Year Myth */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                The 20-Year Myth: A Flawed Approach
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Many insurance carriers base decisions on simplistic and misleading criteria.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Every Roof is Unique</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Roofing systems vary widely in quality, materials, and installation methods. 
                    A well-constructed roof may easily outlast the arbitrary 20-year mark.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Environmental Factors Matter</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    North Carolina's diverse climate means roofs face varying challenges. 
                    A one-size-fits-all age limit fails to account for these crucial differences.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Maintenance Makes a Difference</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Regular maintenance and timely repairs can significantly extend a roof's lifespan. 
                    Blanket policies often ignore the care homeowners put into preserving their roofs.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How We Help */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Home className="w-12 h-12 text-brand-trophy-gold mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How Carolina Trophy Roofs Can Help
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We offer comprehensive Roof Life Expectancy Reports that go beyond simple age calculations.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {keyFeatures.map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-gray-200 hover:border-brand-trophy-gold transition-colors">
                  <CardHeader>
                    <CardTitle className="flex items-center text-brand-charcoal-gray">
                      <feature.icon className="w-6 h-6 mr-3 text-brand-trophy-gold" />
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Service Details */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-charcoal-gray text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Professional Roof Life Expectancy Report
            </h2>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8">
              <div className="text-4xl font-bold text-brand-trophy-gold mb-4">$500</div>
              <p className="text-xl mb-6">Flat Fee - Comprehensive Assessment</p>
              
              <div className="space-y-4 text-left max-w-2xl mx-auto">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-brand-trophy-gold mr-3 mt-1 flex-shrink-0" />
                  <span>Professional inspection by certified roofing experts</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-brand-trophy-gold mr-3 mt-1 flex-shrink-0" />
                  <span>Detailed written report with photographic evidence</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-brand-trophy-gold mr-3 mt-1 flex-shrink-0" />
                  <span>Expert opinion on remaining roof lifespan</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-brand-trophy-gold mr-3 mt-1 flex-shrink-0" />
                  <span>Maintenance recommendations to extend roof life</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-brand-trophy-gold mr-3 mt-1 flex-shrink-0" />
                  <span>Documentation to challenge insurance decisions</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleCallNow}
                size="lg"
                className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-3"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call (252) 371-2266
              </Button>
              <Button 
                onClick={handleContactUs}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-3"
              >
                Schedule Assessment
              </Button>
            </div>
          </div>
        </section>

        {/* Legal Disclaimers */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Important Disclaimers</h2>
            
            <div className="space-y-6 text-sm text-gray-700">
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <strong>Legal Disclaimer:</strong> Carolina Trophy Roofs is not a law firm, and we do not provide legal advice. 
                  We are not licensed insurance claim advocates, and we cannot control the outcome of insurance claims or coverage decisions.
                </AlertDescription>
              </Alert>

              <div className="bg-white p-6 rounded-lg border">
                <h3 className="font-semibold mb-3">Service Terms & Conditions:</h3>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Our Roof Life Expectancy Report is a paid service offered for a flat fee of $500, charged regardless of assessment outcome.</li>
                  <li>We do not guarantee that we will conclude your roof has remaining life - each roof is evaluated individually and objectively.</li>
                  <li>If we determine your roof has remaining life under normal conditions with regular maintenance, we will state this in writing and are prepared to stand by this opinion in court if necessary.</li>
                  <li>Our opinion is based on the roof's condition on the day of inspection. Unforeseen events may change the condition and our opinion.</li>
                  <li>We recommend annual roof inspections to ensure your roof is aging as expected and to catch developing issues early.</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <h3 className="font-semibold mb-3">Legal Consultation Recommendation:</h3>
                <p>
                  We strongly advise anyone needing legal counsel to seek the services of a qualified attorney, 
                  particularly for matters related to insurance claims and policy interpretations. 
                  By engaging our services, you acknowledge and agree to these terms and limitations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reference Sources */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Authoritative Reference Sources
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {referenceUrls.map((ref, index) => (
                <a 
                  key={index}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-brand-trophy-gold hover:shadow-md transition-all"
                >
                  <div className="text-sm font-medium text-brand-charcoal-gray hover:text-brand-trophy-gold">
                    {ref.name}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default RoofLifeExpectancyReport;
